import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import colors from '../../../util/colors';
import { mvs } from '../../../util/metrices';
import Bold from '../../../typography/BoldText';
import Medium from '../../../typography/MediumText';
import Regular from '../../../typography/RegularText';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAuth } from '../../../context/AuthContext';
import ImagePicker from 'react-native-image-crop-picker';
import { requestCameraPermission } from '../../../util/permissions';

const UserProfileScreen = ({ navigation }) => {
  const { user, updateProfile, logout, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [profileImage, setProfileImage] = useState(user?.profile_image || '');

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setEmail(user.email || '');
      setUsername(user.username || '');
      setProfileImage(user.profile_image || '');
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    try {
      setIsLoading(true);
      
      // Validate password fields if attempting to change password
      if (currentPassword || newPassword || confirmPassword) {
        if (!currentPassword) {
          Alert.alert('Error', 'Current password is required to change password');
          return;
        }
        if (!newPassword) {
          Alert.alert('Error', 'New password is required');
          return;
        }
        if (newPassword !== confirmPassword) {
          Alert.alert('Error', 'New passwords do not match');
          return;
        }
        if (newPassword.length < 6) {
          Alert.alert('Error', 'New password must be at least 6 characters long');
          return;
        }
      }
      
      // Update profile information including username and password
      const profileUpdates = {};
      if (fullName !== user?.full_name) profileUpdates.full_name = fullName;
      if (email !== user?.email) profileUpdates.email = email;
      if (username !== user?.username) profileUpdates.username = username;
      
      // Add password fields if changing password
      if (currentPassword && newPassword) {
        profileUpdates.current_password = currentPassword;
        profileUpdates.new_password = newPassword;
      }
      
      // Add profile image if changed
      if (profileImage !== user?.profile_image) {
        profileUpdates.profile_image = profileImage;
      }
      
      if (Object.keys(profileUpdates).length > 0) {
        await updateProfile(profileUpdates);
      }
      
      // Clear password fields after successful update
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setIsEditing(false);
      Alert.alert('Success', 'Profile updated successfully!');
      
      // Refresh user data to get updated info
      await refreshUser();
      
    } catch (error) {
      console.error('Profile update error:', error);
      Alert.alert('Error', error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              await logout();
            } catch (error) {
              console.error('Logout error:', error);
            }
          }
        }
      ]
    );
  };

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      await refreshUser();
    } catch (error) {
      console.error('Refresh error:', error);
      Alert.alert('Error', 'Failed to refresh profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePhoto = async () => {
    try {
      const hasPermission = await requestCameraPermission();
      if (!hasPermission) {
        Alert.alert('Permission Required', 'Camera permission is required to take photos.');
        return;
      }

      Alert.alert(
        'Select Photo',
        'Choose how you want to select a photo',
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Camera', 
            onPress: () => openCamera() 
          },
          { 
            text: 'Gallery', 
            onPress: () => openGallery() 
          }
        ]
      );
    } catch (error) {
      console.error('Photo selection error:', error);
      Alert.alert('Error', 'Failed to open photo selector');
    }
  };

  const openCamera = () => {
    ImagePicker.openCamera({
      width: 400,
      height: 400,
      cropping: true,
      cropperCircleOverlay: true,
      includeBase64: true,
      mediaType: 'photo',
    }).then(image => {
      const base64Image = `data:${image.mime};base64,${image.data}`;
      setProfileImage(base64Image);
    }).catch(error => {
      if (error.code !== 'E_PICKER_CANCELLED') {
        console.error('Camera error:', error);
        Alert.alert('Error', 'Failed to take photo');
      }
    });
  };

  const openGallery = () => {
    ImagePicker.openPicker({
      width: 400,
      height: 400,
      cropping: true,
      cropperCircleOverlay: true,
      includeBase64: true,
      mediaType: 'photo',
    }).then(image => {
      const base64Image = `data:${image.mime};base64,${image.data}`;
      setProfileImage(base64Image);
    }).catch(error => {
      if (error.code !== 'E_PICKER_CANCELLED') {
        console.error('Gallery error:', error);
        Alert.alert('Error', 'Failed to select photo');
      }
    });
  };

  if (!user) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContainer}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft} />
        <Bold style={styles.headerTitle}>Profile</Bold>
        <TouchableOpacity style={styles.refreshButton} onPress={handleRefresh}>
          <Icon name="refresh" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Dashboard Actions */}
      <View style={styles.dashboardSection}>
        <Regular style={styles.sectionTitle}>Author Dashboard</Regular>
        <View style={styles.dashboardActions}>
          <TouchableOpacity 
            style={styles.dashboardButton}
            onPress={() => navigation.navigate('AuthorDashboard')}>
            <Icon name="analytics-outline" size={24} color={colors.primary} />
            <Medium style={styles.dashboardButtonText}>Dashboard</Medium>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.dashboardButton}
            onPress={() => navigation.navigate('EditBio')}>
            <Icon name="create-outline" size={24} color={colors.primary} />
            <Medium style={styles.dashboardButtonText}>Edit Bio</Medium>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.dashboardButton}
            onPress={() => navigation.navigate('ManageDevotions')}>
            <Icon name="library-outline" size={24} color={colors.primary} />
            <Medium style={styles.dashboardButtonText}>Devotions</Medium>
          </TouchableOpacity>
        </View>
      </View>

      {/* Profile Image */}
      <View style={styles.profileImageContainer}>
        <TouchableOpacity 
          style={styles.profileImageWrapper}
          onPress={isEditing ? handleChangePhoto : undefined}
          disabled={!isEditing}>
          {profileImage ? (
            <Image 
              source={{ uri: profileImage }} 
              style={styles.profileImage}
            />
          ) : (
            <View style={styles.profileImagePlaceholder}>
              <Icon name="person" size={60} color={colors.textSecondary} />
            </View>
          )}
          {isEditing && (
            <View style={styles.cameraIconOverlay}>
              <Icon name="camera" size={20} color={colors.textLight} />
            </View>
          )}
        </TouchableOpacity>
        {isEditing && (
          <Medium style={styles.tapToChangeText}>Tap photo to change</Medium>
        )}
        <Medium style={styles.username}>@{user.username}</Medium>
        <Regular style={styles.joinDate}>
          Member since {new Date(user.created_at).toLocaleDateString()}
        </Regular>
      </View>

      {/* Profile Form */}
      <View style={styles.formContainer}>
        {/* Basic Information Section */}
        <View style={styles.sectionHeader}>
          <Bold style={styles.sectionTitle}>Basic Information</Bold>
          {!isEditing && (
            <Regular style={styles.sectionSubtitle}>Tap edit to modify your profile</Regular>
          )}
        </View>
        
        <View style={styles.inputGroup}>
          <Regular style={styles.label}>Full Name</Regular>
          {isEditing ? (
            <View style={styles.inputContainer}>
              <Icon name="person-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={fullName}
                onChangeText={setFullName}
                placeholder="Enter your full name"
                placeholderTextColor={colors.textSecondary}
              />
            </View>
          ) : (
            <View style={styles.displayContainer}>
              <Icon name="person-outline" size={18} color={colors.textSecondary} />
              <Medium style={styles.displayText}>{fullName || 'Not set'}</Medium>
            </View>
          )}
        </View>

        <View style={styles.inputGroup}>
          <Regular style={styles.label}>Username</Regular>
          {isEditing ? (
            <View style={styles.inputContainer}>
              <Icon name="at-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={username}
                onChangeText={setUsername}
                placeholder="Enter your username"
                placeholderTextColor={colors.textSecondary}
                autoCapitalize="none"
              />
            </View>
          ) : (
            <View style={styles.displayContainer}>
              <Icon name="at-outline" size={18} color={colors.textSecondary} />
              <Medium style={styles.displayText}>{username || 'Not set'}</Medium>
            </View>
          )}
        </View>

        <View style={styles.inputGroup}>
          <Regular style={styles.label}>Email</Regular>
          {isEditing ? (
            <View style={styles.inputContainer}>
              <Icon name="mail-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                placeholder="Enter your email"
                placeholderTextColor={colors.textSecondary}
                keyboardType="email-address"
              />
            </View>
          ) : (
            <View style={styles.displayContainer}>
              <Icon name="mail-outline" size={18} color={colors.textSecondary} />
              <Medium style={styles.displayText}>{email || 'Not set'}</Medium>
            </View>
          )}
        </View>

        {/* Password Change Section */}
        {isEditing && (
          <>
            <View style={styles.passwordSection}>
              <View style={styles.passwordHeader}>
                <Regular style={styles.label}>Change Password</Regular>
                <TouchableOpacity 
                  style={styles.showPasswordsButton}
                  onPress={() => setShowPasswords(!showPasswords)}>
                  <Icon 
                    name={showPasswords ? 'eye-outline' : 'eye-off-outline'} 
                    size={20} 
                    color={colors.primary} 
                  />
                  <Regular style={styles.showPasswordsText}>
                    {showPasswords ? 'Hide' : 'Show'}
                  </Regular>
                </TouchableOpacity>
              </View>
              
              <View style={styles.inputGroup}>
                <Regular style={styles.passwordLabel}>Current Password</Regular>
                <View style={styles.inputContainer}>
                  <Icon name="lock-closed-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    value={currentPassword}
                    onChangeText={setCurrentPassword}
                    placeholder="Enter current password"
                    placeholderTextColor={colors.textSecondary}
                    secureTextEntry={!showPasswords}
                    autoCapitalize="none"
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Regular style={styles.passwordLabel}>New Password</Regular>
                <View style={styles.inputContainer}>
                  <Icon name="lock-closed-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    value={newPassword}
                    onChangeText={setNewPassword}
                    placeholder="Enter new password"
                    placeholderTextColor={colors.textSecondary}
                    secureTextEntry={!showPasswords}
                    autoCapitalize="none"
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Regular style={styles.passwordLabel}>Confirm New Password</Regular>
                <View style={styles.inputContainer}>
                  <Icon name="lock-closed-outline" size={20} color={colors.textSecondary} style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    placeholder="Confirm new password"
                    placeholderTextColor={colors.textSecondary}
                    secureTextEntry={!showPasswords}
                    autoCapitalize="none"
                  />
                </View>
              </View>
            </View>
          </>
        )}

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          {isEditing ? (
            <View style={styles.editButtonGroup}>
              <TouchableOpacity
                style={[styles.actionButton, styles.cancelButton]}
                onPress={() => {
                  setIsEditing(false);
                  // Reset to original values
                  setFullName(user?.full_name || '');
                  setEmail(user?.email || '');
                  setUsername(user?.username || '');
                  setProfileImage(user?.profile_image || '');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                disabled={isLoading}>
                <Medium style={styles.cancelButtonText}>Cancel</Medium>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.actionButton, styles.saveButton]}
                onPress={handleUpdateProfile}
                disabled={isLoading}>
                {isLoading ? (
                  <ActivityIndicator color={colors.textLight} size="small" />
                ) : (
                  <Medium style={styles.saveButtonText}>Save</Medium>
                )}
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={[styles.actionButton, styles.editButton]}
              onPress={() => setIsEditing(true)}>
              <Icon name="create-outline" size={20} color={colors.textLight} style={styles.buttonIcon} />
              <Medium style={styles.editButtonText}>Edit Profile</Medium>
            </TouchableOpacity>
          )}
        </View>

        {/* Logout Button */}
        <TouchableOpacity
          style={[styles.actionButton, styles.logoutButton]}
          onPress={handleLogout}>
          <Icon name="log-out-outline" size={20} color={colors.error} style={styles.buttonIcon} />
          <Medium style={styles.logoutButtonText}>Logout</Medium>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContainer: {
    paddingBottom: mvs(30),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: mvs(20),
    paddingTop: mvs(50),
    paddingBottom: mvs(20),
  },
  headerLeft: {
    width: 40, // Same width as refreshButton to center the title
  },
  headerTitle: {
    fontSize: mvs(20),
    color: colors.black,
  },
  refreshButton: {
    padding: mvs(8),
  },
  dashboardSection: {
    backgroundColor: colors.surface,
    marginHorizontal: mvs(20),
    marginTop: mvs(20),
    marginBottom: mvs(16),
    borderRadius: mvs(12),
    padding: mvs(16),
    shadowColor: colors.black,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: mvs(18),
    color: colors.textPrimary,
    marginBottom: mvs(16),
    fontWeight: '600',
  },
  dashboardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dashboardButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: mvs(12),
    paddingHorizontal: mvs(8),
    backgroundColor: colors.background,
    borderRadius: mvs(8),
    marginHorizontal: mvs(4),
    borderWidth: 1,
    borderColor: colors.accent,
  },
  dashboardButtonText: {
    color: colors.primary,
    fontSize: mvs(12),
    marginTop: mvs(4),
    textAlign: 'center',
  },
  profileImageContainer: {
    alignItems: 'center',
    paddingHorizontal: mvs(20),
    marginBottom: mvs(30),
  },
  profileImagePlaceholder: {
    width: mvs(120),
    height: mvs(120),
    borderRadius: mvs(60),
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: mvs(16),
    borderWidth: 2,
    borderColor: colors.accent,
  },
  username: {
    fontSize: mvs(18),
    color: colors.primary,
    marginBottom: mvs(4),
  },
  joinDate: {
    fontSize: mvs(14),
    color: colors.textSecondary,
  },
  formContainer: {
    paddingHorizontal: mvs(20),
  },
  sectionHeader: {
    marginBottom: mvs(20),
    paddingBottom: mvs(12),
    borderBottomWidth: 1,
    borderBottomColor: colors.accent,
  },
  sectionTitle: {
    fontSize: mvs(18),
    color: colors.textPrimary,
    marginBottom: mvs(4),
  },
  sectionSubtitle: {
    fontSize: mvs(14),
    color: colors.textSecondary,
  },
  inputGroup: {
    marginBottom: mvs(24),
  },
  label: {
    fontSize: mvs(12),
    color: colors.textSecondary,
    marginBottom: mvs(6),
    fontWeight: '400',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: mvs(12),
    paddingHorizontal: mvs(16),
    height: mvs(56),
    borderWidth: 1,
    borderColor: colors.accent,
  },
  displayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: mvs(4),
    paddingVertical: mvs(8),
    minHeight: mvs(44),
  },
  inputIcon: {
    marginRight: mvs(12),
  },
  input: {
    flex: 1,
    fontSize: mvs(16),
    color: colors.black,
    fontFamily: 'DMSans-Regular',
  },
  displayText: {
    flex: 1,
    fontSize: mvs(18),
    color: colors.text,
    fontFamily: 'DMSans-Medium',
    marginLeft: mvs(8),
    fontWeight: '600',
  },
  buttonContainer: {
    marginTop: mvs(10),
  },
  editButtonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: mvs(16),
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: mvs(12),
    height: mvs(56),
    paddingHorizontal: mvs(20),
  },
  editButton: {
    backgroundColor: colors.primary,
    marginBottom: mvs(16),
  },
  saveButton: {
    backgroundColor: colors.primary,
    flex: 1,
    marginLeft: mvs(8),
  },
  cancelButton: {
    backgroundColor: colors.accent,
    flex: 1,
    marginRight: mvs(8),
  },
  logoutButton: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.error,
  },
  buttonIcon: {
    marginRight: mvs(8),
  },
  editButtonText: {
    color: colors.textLight,
    fontSize: mvs(16),
  },
  saveButtonText: {
    color: colors.textLight,
    fontSize: mvs(16),
  },
  cancelButtonText: {
    color: colors.textSecondary,
    fontSize: mvs(16),
  },
  logoutButtonText: {
    color: colors.error,
    fontSize: mvs(16),
  },
  profileImageWrapper: {
    position: 'relative',
    marginBottom: mvs(12),
  },
  profileImage: {
    width: mvs(120),
    height: mvs(120),
    borderRadius: mvs(60),
    borderWidth: 3,
    borderColor: colors.primary,
  },
  cameraIconOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: colors.primary,
    borderRadius: mvs(20),
    width: mvs(40),
    height: mvs(40),
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: colors.white,
  },
  passwordSection: {
    backgroundColor: colors.accent,
    borderRadius: mvs(12),
    padding: mvs(16),
    marginBottom: mvs(16),
  },
  passwordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: mvs(16),
  },
  showPasswordsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: mvs(8),
    paddingVertical: mvs(4),
  },
  showPasswordsText: {
    color: colors.primary,
    fontSize: mvs(14),
    marginLeft: mvs(4),
  },
  passwordLabel: {
    fontSize: mvs(12),
    color: colors.textSecondary,
    marginBottom: mvs(6),
    fontWeight: '400',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tapToChangeText: {
    fontSize: mvs(12),
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: mvs(8),
  },
});

export default UserProfileScreen;
